import DateRangeFilter from '@/components/cards/FilterCard/dateRangeFilter/DateRangeFilter';
import ShowsFilter from '@/components/cards/FilterCard/showsFilter/ShowsFilter';
import { useDerivedNetworkContext } from '@/context';
import type { DateRange, ShowFilter } from '@/types';
import type { ReactNode } from 'react';

type ModalBodyProps = {
  showShowsFilter: boolean;
  showDateRangeFilter: boolean;
  modalDateRange: DateRange;
  modalSelectedShow: ShowFilter;
  handleDateRangeApply: (dateRange: DateRange) => void;
  children: (modalDateRange: DateRange, modalSelectedShow: ShowFilter) => ReactNode;
  actions?: (modalDateRange: DateRange, modalSelectedShow: ShowFilter) => ReactNode;
  onDateRangeOpen?: () => void;
  onDateRangePresetSelect?: (preset: string) => void;
  onShowFilterOpen?: () => void;
  onShowFilterApply: (selectedShow: ShowFilter) => void;
};

/**
 * This component is used to render the body of a modal.
 * @param showShowsFilter - Whether to show the shows filter.
 * @param showDateRangeFilter - Whether to show the date range filter.
 * @param modalDateRange - The date range of the modal.
 * @param modalSelectedShow - The selected show of the modal.
 * @param onShowFilterApply - The function to handle the selected show apply.
 * @param handleDateRangeApply - The function to handle the date range apply.
 * @param children - The children of the modal.
 * @param actions - Optional actions to render next to the modal filters.
 * @param onDateRangeOpen - The function to handle the date range open.
 * @param onDateRangePresetSelect - The function to handle the date range preset select.
 * @param onShowFilterOpen - The function to handle the shows filter open.
 * @param onShowFilterApply - The function to handle the shows filter apply.
 * @returns The modal body component.
 */
const ModalBody = ({
  showShowsFilter,
  showDateRangeFilter,
  modalDateRange,
  modalSelectedShow,
  handleDateRangeApply,
  onDateRangeOpen,
  onDateRangePresetSelect,
  onShowFilterOpen,
  onShowFilterApply,
  children,
  actions,
}: ModalBodyProps) => {
  const { shows } = useDerivedNetworkContext();

  return (
    <>
      {(showShowsFilter || showDateRangeFilter) && (
        <div className="flex flex-nowrap items-center gap-4 overflow-x-auto border-b border-b-[#DEDEF2] p-6">
          {showShowsFilter && (
            <ShowsFilter
              shows={shows}
              selectedShow={modalSelectedShow}
              onApply={onShowFilterApply}
              onOpen={onShowFilterOpen}
            />
          )}
          {showDateRangeFilter && (
            <DateRangeFilter
              dateRange={{
                from: modalDateRange.from,
                to: modalDateRange.to,
              }}
              onApply={(from, to) => handleDateRangeApply({ from, to })}
              onOpen={onDateRangeOpen}
              onPresetSelect={onDateRangePresetSelect}
            />
          )}
          {actions?.(modalDateRange, modalSelectedShow)}
        </div>
      )}
      {children(modalDateRange, modalSelectedShow)}
    </>
  );
};

export default ModalBody;
